/**
 * 공통 유틸리티 함수 모음
 * 모든 Vue 컴포넌트에서 공통으로 사용하는 함수들을 정의합니다.
 * main.js에서 전역으로 등록되므로 별도 import 없이 this.$utils 또는 개별 함수로 사용 가능
 */

/**
 * 숫자를 천단위 콤마로 포맷팅
 * @param {number|string} value - 포맷팅할 숫자
 * @returns {string} 포맷팅된 문자열 (예: "1,234,567")
 */
export function formatNumber(value) {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return '0';
  }
  return num.toLocaleString('ko-KR');
}

/**
 * 날짜를 YYYY-MM-DD 형식으로 포맷팅
 * @param {string|Date} dateStr - 포맷팅할 날짜 (YYYYMMDD 또는 Date 객체)
 * @returns {string} 포맷팅된 날짜 문자열 (예: "2024-01-15")
 */
export function formatDate(dateStr) {
  if (!dateStr) return '';
  
  // Date 객체인 경우
  if (dateStr instanceof Date) {
    const year = dateStr.getFullYear();
    const month = String(dateStr.getMonth() + 1).padStart(2, '0');
    const day = String(dateStr.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // 문자열인 경우 (YYYYMMDD 형식)
  const str = String(dateStr).replace(/[^0-9]/g, '');
  if (str.length === 8) {
    return `${str.substring(0, 4)}-${str.substring(4, 6)}-${str.substring(6, 8)}`;
  }
  
  return dateStr;
}

/**
 * contextPath 추출 함수
 * 현재 페이지 경로에서 Vite 빌드 서빙 prefix (/accounting/app/...) 앞의 context-path 추출
 * @returns {string} contextPath (예: '/accounting' 또는 '')
 */
export function resolveContextPath() {
  const marker = '/app/';
  const path = window.location.pathname;
  const idx = path.indexOf(marker);
  return idx > 0 ? path.substring(0, idx) : '';
}

/**
 * 금액을 원화 형식으로 포맷팅 (단위 포함)
 * @param {number|string} value - 포맷팅할 금액
 * @returns {string} 포맷팅된 문자열 (예: "1,234,567 원")
 */
export function formatCurrency(value) {
  return `${formatNumber(value)} 원`;
}

/**
 * 퍼센트 값을 포맷팅
 * @param {number|string} value - 포맷팅할 퍼센트 값
 * @param {number} decimals - 소수점 자릿수 (기본값: 2)
 * @returns {string} 포맷팅된 문자열 (예: "8.50%")
 */
export function formatPercent(value, decimals = 2) {
  if (value === null || value === undefined || value === '') {
    return '0%';
  }
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return '0%';
  }
  return `${num.toFixed(decimals)}%`;
}

/**
 * 금액 입력 필드용: 입력값에서 숫자만 추출하여 천단위 쉼표 표시 문자열로 변환
 * @param {string|number} value - 입력값
 * @returns {{ display: string, raw: number }} display: 쉼표 포맷 문자열, raw: 순수 숫자
 * @example
 *   const { display, raw } = formatMoneyInput('1234567');
 *   // display = '1,234,567', raw = 1234567
 */
export function formatMoneyInput(value) {
  const str = String(value || '').replace(/[^0-9]/g, '');
  const raw = str ? parseInt(str, 10) : 0;
  var display = raw ? raw.toLocaleString('ko-KR') : '';
  return { display: display, raw: raw };
}

/**
 * 쉼표가 포함된 금액 문자열에서 순수 숫자 추출
 * @param {string|number} value - 쉼표 포함 문자열 (예: "1,234,567")
 * @returns {number} 순수 숫자 (예: 1234567)
 */
export function parseMoneyInput(value) {
  if (value === null || value === undefined || value === '') return 0;
  var str = String(value).replace(/[^0-9]/g, '');
  return str ? parseInt(str, 10) : 0;
}

/**
 * 금액 입력 이벤트 핸들러: input 이벤트에서 호출하여 obj[field]에 숫자 저장 + 화면에 쉼표 표시
 * @param {Event} e - input 이벤트
 * @param {object} obj - 반응형 객체 (reactive 또는 ref.value)
 * @param {string} field - 필드명
 * @example
 *   <input type="text" :value="form.amt ? formatNumber(form.amt) : ''" @input="e => onMoneyInput(e, form, 'amt')">
 */
export function onMoneyInput(e, obj, field) {
  var result = formatMoneyInput(e.target.value);
  obj[field] = result.raw;
  e.target.value = result.display;
}

/**
 * API 호출 공통 함수
 *
 * 모든 /api/** 요청에 사용자가 선택한 RMS 세션(X-RMS-Model-Id) 헤더를 자동 주입한다.
 * (백엔드는 RMS TR 송신 경로에서만 이 헤더를 해석하므로 다른 API 에는 영향 없음)
 *
 * @param {string} url - API URL
 * @param {object} options - fetch options
 * @returns {Promise<any>} API 응답 데이터
 */
export async function fetchApi(url, options = {}) {
  // X-RMS-Model-Id 헤더 주입 (localStorage 기반 — composable 순환 의존성 회피)
  let rmsModelId = null;
  try {
    rmsModelId = localStorage.getItem('rms.session.modelId');
  } catch (e) {
    // private 모드 등 — 무시
  }

  const defaultOptions = {
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(rmsModelId ? { 'X-RMS-Model-Id': rmsModelId } : {}),
      ...options.headers
    }
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * 배열을 CSV로 다운로드
 * @param {Array} data - 다운로드할 데이터 배열
 * @param {string} filename - 파일명 (기본값: 'download.csv')
 */
export function downloadCSV(data, filename = 'download.csv') {
  if (!data || data.length === 0) {
    alert('다운로드할 데이터가 없습니다.');
    return;
  }
  
  // CSV 문자열 생성
  const headers = Object.keys(data[0]).join(',');
  const rows = data.map(row => 
    Object.values(row).map(val => 
      typeof val === 'string' && val.includes(',') ? `"${val}"` : val
    ).join(',')
  );
  const csv = [headers, ...rows].join('\n');
  
  // BOM 추가 (한글 깨짐 방지)
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  
  // 다운로드 링크 생성 및 클릭
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  
  // 메모리 해제
  URL.revokeObjectURL(link.href);
}

/**
 * 상태 배지 클래스 반환
 * @param {string} status - 상태 코드
 * @returns {string} Bootstrap 배지 클래스
 */
export function getStatusBadgeClass(status) {
  const classMap = {
    'normal': 'badge bg-success',
    'active': 'badge bg-success',
    'maturity': 'badge bg-info',
    'early': 'badge bg-warning',
    'forced': 'badge bg-danger',
    'losscut': 'badge bg-dark',
    'pending': 'badge bg-warning',
    'approved': 'badge bg-primary',
    'rejected': 'badge bg-danger',
    'completed': 'badge bg-secondary',
    // 대출상태값
    '00': 'badge bg-success',
    '10': 'badge bg-secondary',
    // '11': 'badge bg-primary',
    '12': 'badge bg-secondary',
    '13': 'badge bg-warning',
    '14': 'badge bg-danger',
    '15': 'badge bg-danger',
    // 상환방식코드
    '01': 'badge bg-primary',
    '02': 'badge bg-info',
    '03': 'badge bg-success',
  };
  return classMap[status] || 'badge bg-secondary';
}

// 시각 포맷팅 (HHMMSS -> HH:MM:SS)
export function formatTime(time) {
  if (!time || time.length !== 6) return time;
  return `${time.substring(0, 2)}:${time.substring(2, 4)}:${time.substring(4, 6)}`;
};

/**
 * KCB 입사년월 등 YYYYmm(6자리) → "YYYY년 MM월" 변환
 * @param {string} str - 6자리 년월 문자열 (예: "202001")
 * @returns {string} 포맷팅된 문자열 (예: "2020년 01월")
 */
export function formatYearMonth(str) {
  if (!str) return '-';
  const s = String(str).replace(/[^0-9]/g, '');
  if (s.length === 6) {
    return `${s.substring(0, 4)}년 ${s.substring(4, 6)}월`;
  }
  return str;
}

/**
 * 전화번호 하이픈 포맷 (010-XXXX-XXXX, 02-XXX(X)-XXXX 등)
 * @param {string} str - 숫자만 포함된 전화번호 (예: "01012345678", "0212345678")
 * @returns {string} 하이픈 포함 전화번호 (예: "010-1234-5678", "02-1234-5678")
 */
export function formatPhone(str) {
  if (!str) return '-';
  const s = String(str).replace(/[^0-9]/g, '');
  if (s.length === 11) {
    return `${s.substring(0, 3)}-${s.substring(3, 7)}-${s.substring(7, 11)}`;
  }
  if (s.length === 10) {
    if (s.startsWith('02')) {
      return `${s.substring(0, 2)}-${s.substring(2, 6)}-${s.substring(6, 10)}`;
    }
    return `${s.substring(0, 3)}-${s.substring(3, 6)}-${s.substring(6, 10)}`;
  }
  if (s.length === 9 && s.startsWith('02')) {
    return `${s.substring(0, 2)}-${s.substring(2, 5)}-${s.substring(5, 9)}`;
  }
  return str;
}

/**
 * 토스트 알림 표시
 * @param {string} message - 표시할 메시지
 * @param {'success'|'error'|'warning'|'info'} type - 알림 타입 (기본값: 'info')
 * @param {number} duration - 표시 시간(ms) (기본값: 3000)
 */
export function showToast(message, type = 'info', duration = 3000) {
  // 토스트 컨테이너 확인/생성
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.style.cssText = 'position:fixed;top:20px;right:20px;z-index:9999;display:flex;flex-direction:column;gap:8px;';
    document.body.appendChild(container);
  }

  const colorMap = {
    success: { bg: '#198754', icon: '\u2713' },
    error:   { bg: '#dc3545', icon: '\u2717' },
    warning: { bg: '#ffc107', icon: '\u26A0', color: '#000' },
    info:    { bg: '#0d6efd', icon: '\u2139' }
  };
  const cfg = colorMap[type] || colorMap.info;

  const toast = document.createElement('div');
  toast.style.cssText = `background:${cfg.bg};color:${cfg.color || '#fff'};padding:12px 20px;border-radius:8px;`
    + 'box-shadow:0 4px 12px rgba(0,0,0,0.15);font-size:14px;max-width:400px;'
    + 'display:flex;align-items:center;gap:8px;opacity:0;transition:opacity 0.3s;cursor:pointer;';
  const iconSpan = document.createElement('span');
  iconSpan.style.cssText = 'font-size:16px;font-weight:bold';
  iconSpan.textContent = cfg.icon;
  const msgSpan = document.createElement('span');
  msgSpan.textContent = message;
  toast.appendChild(iconSpan);
  toast.appendChild(msgSpan);
  toast.onclick = () => removeToast(toast);

  container.appendChild(toast);
  requestAnimationFrame(() => { toast.style.opacity = '1'; });

  setTimeout(() => removeToast(toast), duration);

  function removeToast(el) {
    el.style.opacity = '0';
    setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 300);
  }
}

/**
 * API 에러 메시지 추출 후 토스트로 표시
 * @param {Error|object} error - axios/fetch 에러 객체
 * @param {string} fallback - 기본 에러 메시지
 */
export function showApiError(error, fallback = '처리 중 오류가 발생했습니다.') {
  const msg = error?.response?.data?.message || error?.message || fallback;
  showToast(msg, 'error', 4000);
}

/**
 * 데이터를 Excel(.xlsx) 파일로 다운로드
 * @param {Array} columns - 컬럼 정의 배열 [{ header, key, width, type }]
 * @param {Array} data - 다운로드할 데이터 배열
 * @param {string} filename - 파일명 (확장자 제외)
 */
export async function downloadExcel(columns, data, filename = 'download') {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(filename);

  sheet.columns = columns.map(col => ({
    header: col.header,
    key: col.key,
    width: col.width || 15,
  }));

  data.forEach(row => sheet.addRow(row));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.xlsx`;
  link.click();
  URL.revokeObjectURL(link.href);
}
